import type {
  LocationQuery,
  LocationQueryValue,
  RouteLocationNormalized,
  UseLinkReturn,
} from 'vue-router'
import type { NavigationOptions } from '@/components/BaseButton.vue'
import { isEqual } from '@/utils/is-equal.js'
import { isValidNumericString } from '@/utils/number.js'

export type NavigateToInput = Parameters<typeof navigateTo>[0]
export type NavigateToOptions = Parameters<typeof navigateTo>[1]

/**
 * Convert a `target_url` from the Sidebar API to an internal URL.
 * So it starts with `/` and does not cause a reload when navigating to it.
 */
export function getInternalUrl(targetURL: string) {
  const config = useRuntimeConfig()
  return new URL(targetURL).pathname.replace(config.app.baseURL, '/')
}

/**
 * Gets the absolute path for internal frontend routes, including the app's base URL.
 * This handles both development (no base path) and production (`/next/` base path).
 *
 * Note: This function is primarily needed when using `navigateTo` with the `target` option
 * (e.g., `target: '_blank'`), where neither relative paths nor named routes work properly.
 *
 * @param path - The internal route path (should start with `/`)
 * @returns The internal URL with base URL prepended (absolute path)
 */
export function getAbsoluteInternalUrl(path: string) {
  const config = useRuntimeConfig()

  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  // In development, config.app.baseURL is typically '/'
  // In production, it's '/next/' (or whatever NUXT_APP_BASE_URL is set to)
  const baseURL = config.app.baseURL || '/'

  // Remove trailing slash from baseURL to avoid double slashes
  const cleanBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL

  return `${cleanBaseURL}${normalizedPath}`
}

/**
 * Validates if a query value is a valid numeric ID value.
 * A valid numeric ID value is a string with only numbers, no decimals or negative signs.
 *
 * @param param The query value to validate
 * @returns true if the query value is a valid numeric ID, false otherwise
 */
export function isValidQueryValueNumericId(
  param: LocationQueryValue | LocationQueryValue[] | undefined,
) {
  return isValidNumericString(param?.toString() || '')
}

/**
 * Validates that a route parameter exists in route params and is a valid numeric ID.
 * A valid numeric ID value is a string with only numbers, no decimals or negative signs.
 *
 * @param route - The route object containing `params`
 * @param paramName - The name of the route parameter to validate
 * @returns true if route parameter exists and it's value is a valid numeric ID
 */
export function isValidRouteParamNumericId(
  route: Pick<RouteLocationNormalized, 'params'> & {
    params: {
      [key: string]: LocationQueryValue | LocationQueryValue[] | undefined
    }
  },
  paramName: string,
) {
  return (
    paramName in route.params &&
    isValidQueryValueNumericId(route.params[paramName])
  )
}

/**
 * Validates URL query parameters and redirects if any fail validation
 * @param route The route object containing path and query
 * @param validations Array of parameter validations
 * @returns A navigation or undefined if no redirection needed
 */
export function sanitizeQueryParams({
  route,
  validations = [],
}: {
  route: {
    path: string
    query: LocationQuery
  }
  validations?: Array<{
    paramName: string
    validate?: (value: string) => boolean
    defaultValue?: LocationQueryValue | LocationQueryValue[] | undefined
  }>
}) {
  const newQuery = { ...route.query }

  // Apply each validation to corresponding parameters
  validations.forEach(({ paramName, validate, defaultValue }) => {
    // Only validate if the parameter exists
    if (paramName in route.query) {
      const paramValue = route.query[paramName]
      const paramValueArray = paramValue?.toString().split(',') || []

      // Run custom validation to each of the parameter values
      const invalidValues = !validate
        ? []
        : paramValueArray.filter((value) => !validate(value))

      const isValid = !invalidValues.length
      if (isValid) {
        return true
      }

      // Define fallback value
      const cleanParamValue = paramValueArray.filter(
        (value) => !invalidValues.includes(value),
      )
      const fallbackValue = cleanParamValue?.length
        ? // If valid values are defined, simply remove the invalid values
          cleanParamValue
        : // If after cleanup the parameter is empty, use the default value
          defaultValue

      // Update the query parameter with the fallback value
      newQuery[paramName] = fallbackValue as
        | LocationQueryValue
        | LocationQueryValue[]
    }
  })

  // Return navigation object if changes were made, otherwise undefined
  if (!isEqual(route.query, newQuery)) {
    return navigateTo({
      path: route.path,
      query: newQuery,
      replace: true,
    })
  }

  return undefined
}

/**
 * Builds navigation arguments from NavigationOptions for use with router components
 * @param navigationData - Navigation options containing to, external, and target properties
 * @param router - Optional Vue Router instance for resolving external links
 * @returns Navigation arguments object or undefined if no 'to' property
 */
export function buildNavigationArgs(navigationData: NavigationOptions) {
  if (!navigationData?.to) {
    return
  }

  // If the link is external, we provide both `to` and `href`.
  // This is useful for components that need to know the full URL path to the target page.
  return {
    to: navigationData.to,
    ...(navigationData?.external && {
      external: true,
    }),
    ...(navigationData?.target && {
      open: {
        target: navigationData.target,
      },
    }),
  }
}

/**
 * Workaround for NuxtLink navigation limitations with external links and target attributes.
 *
 * When wrapping components with href attributes in NuxtLink, the `navigate` function from
 * NuxtLink's slotted properties fails to handle external links that also specify a `target`
 * attribute (like target="_blank"). This function provides the necessary fallback logic
 * by using `navigateTo()` for these problematic cases while preserving NuxtLink's default
 * behavior for standard internal navigation.
 *
 * @param href - The href of the link.
 * @param navigate - The navigate function from `NuxtLink` slotted properties.
 * @param options - The options of the link.
 */
export function handleNuxtLinkSlotNavigation(
  href: string,
  navigate: UseLinkReturn['navigate'],
  options: Pick<NavigationOptions, 'external' | 'target'>,
) {
  if (!href) {
    return
  }

  const hasTarget = options.target && options.target !== '_self'
  const needsNavigateTo = options.external || hasTarget

  if (needsNavigateTo) {
    const navigationOptions = {
      ...(options.external && {
        external: true,
      }),
      ...(hasTarget && {
        open: {
          target: options.target! as NonNullable<
            NonNullable<NavigateToOptions>['open']
          >['target'],
          // Add security options for target="_blank"
          ...(options.target === '_blank' && {
            windowFeatures: {
              noopener: true,
              noreferrer: true,
            },
          }),
        },
      }),
    } satisfies NavigateToOptions

    // NuxtLink's `navigate()` (from the arguments) cannot handle external URLs
    // or the `target` attribute. Therefore, we use `navigateTo()` for these cases:
    // - External URLs (options.external)
    // - Navigations with a `target` attribute that's not "_self".
    return navigateTo(href, navigationOptions)
  }

  // For internal redirects without external URLs or a `target` attribute,
  // we use NuxtLink's `navigate()`, respecting NuxtLink's default behavior.
  return navigate()
}
